import { useCallback, useMemo } from 'react'
import { Link } from 'react-router-dom'
import { ChevronLeft, ShieldX } from 'lucide-react'
import { useT } from '@/i18n'
import { PageHeader } from '@/shell/AppShell'
import { DataTable } from '@/components/DataTable'
import type { AdminColumn, FetchParams } from '@/components/DataTable/types'
import { fetchRejectedVerifications, type AdminVerification } from '@/lib/adapters'
import { PERMISSIONS } from '@/lib/permissions'

/**
 * A9.13 — Soxta / rad etilgan verifikatsiya arizalari. GET /admin/verification,
 * status=rejected (client-side filtr). Rad-sababi (decision_note) bilan.
 */
export function VerificationRejected() {
  const t = useT()

  const columns = useMemo<AdminColumn<AdminVerification>[]>(
    () => [
      { key: 'id', header: t.col.id, type: 'id', width: 70 },
      {
        key: 'user_id',
        header: t.col.name,
        type: 'text',
        width: 150,
        sortable: false,
        render: (r) => (
          <Link to={`/users/${r.user_id}`} className="text-accent hover:underline">
            {r.username ? `@${r.username}` : `#${r.user_id}`}
          </Link>
        ),
      },
      {
        key: 'kind',
        header: t.verif.kind,
        type: 'enum',
        width: 120,
        options: [
          { value: 'identity', label: t.verif.kindIdentity, status: 'review' },
          { value: 'org', label: t.verif.kindOrg, status: 'review' },
          { value: 'notable', label: t.verif.kindNotable, status: 'review' },
        ],
      },
      { key: 'evidence', header: t.verif.evidence, type: 'longtext', minWidth: 160 },
      { key: 'decision_note', header: t.common.reason, type: 'longtext', minWidth: 160 },
      { key: 'reviewed_at', header: t.col.time, type: 'date', width: 160 },
    ],
    [t],
  )

  const fetcher = useCallback((params: FetchParams) => fetchRejectedVerifications(params), [])

  return (
    <>
      <PageHeader
        title={t.verif.rejectedTitle}
        screenId="A9.13"
        actions={
          <Link
            to="/verification"
            className="inline-flex items-center gap-1 rounded-md border border-border-default bg-surface-2 px-2.5 py-1 text-sm text-secondary hover:text-primary"
          >
            <ChevronLeft size={13} />
            {t.verif.queue}
          </Link>
        }
      />
      <DataTable<AdminVerification>
        screenId="A9.13"
        permission={PERMISSIONS.USERS_VIEW}
        columns={columns}
        fetcher={fetcher}
        rowId={(r) => String(r.id)}
        emptyState={{ icon: <ShieldX size={28} strokeWidth={1.5} /> }}
      />
    </>
  )
}
